interface AlertData {
  componentName?: string;
  endpoint?: string;
  count: number;
  timestamp: number;
}

interface PerformanceThresholds {
  renderCount: number;
  apiCallCount: number;
  memoryUsage: number;
}

class PerformanceMonitor {
  private renderCounts = new Map<string, number>();
  private apiCallCounts = new Map<string, number>();
  private memorySnapshots: number[] = [];
  private alertCallbacks: ((type: string, data: AlertData) => void)[] = [];
  
  private readonly thresholds: PerformanceThresholds = {
    renderCount: 10, // Alert if component renders > 10 times in 1 second
    apiCallCount: 5,  // Alert if API called > 5 times in 1 second
    memoryUsage: 100 * 1024 * 1024, // 100MB threshold
  };

  constructor() {
    // Clean up old entries every 10 seconds to reduce overhead
    setInterval(() => this.cleanup(), 10000);
    
    // Monitor memory usage every 60 seconds to reduce overhead
    if (typeof window !== 'undefined' && 'performance' in window) {
      setInterval(() => this.checkMemoryUsage(), 60000);
    }
  }

  trackRender(componentName: string): void {
    const now = Date.now();
    const key = `${componentName}-${Math.floor(now / 1000)}`;
    
    const count = (this.renderCounts.get(key) || 0) + 1;
    this.renderCounts.set(key, count);
    
    if (count > this.thresholds.renderCount) {
      console.error(`[PERF WARNING] ${componentName} rendered ${count} times in 1 second`);
      this.sendAlert('excessive-renders', { 
        componentName, 
        count, 
        timestamp: now 
      });
    }

    // Development logging
    if (process.env.NODE_ENV === 'development') {
      console.log(`[RENDER] ${componentName} - Count: ${count}`);
    }
  }

  trackAPICall(endpoint: string): void {
    const now = Date.now();
    const key = `${endpoint}-${Math.floor(now / 1000)}`;
    
    const count = (this.apiCallCounts.get(key) || 0) + 1;
    this.apiCallCounts.set(key, count);
    
    if (count > this.thresholds.apiCallCount) {
      console.error(`[API WARNING] ${endpoint} called ${count} times in 1 second`);
      this.sendAlert('excessive-api-calls', { 
        endpoint, 
        count, 
        timestamp: now 
      });
    }

    // Development logging
    if (process.env.NODE_ENV === 'development') {
      console.log(`[API CALL] ${endpoint} - Count: ${count}`);
    }
  }

  private checkMemoryUsage(): void {
    if (typeof window !== 'undefined' && 'performance' in window && 'memory' in (window.performance as any)) {
      const memory = (window.performance as any).memory;
      const usedJSHeapSize = memory.usedJSHeapSize;
      
      this.memorySnapshots.push(usedJSHeapSize);
      
      // Keep only last 10 snapshots
      if (this.memorySnapshots.length > 10) {
        this.memorySnapshots.shift();
      }
      
      if (usedJSHeapSize > this.thresholds.memoryUsage) {
        console.warn(`[MEMORY WARNING] High memory usage: ${(usedJSHeapSize / 1024 / 1024).toFixed(2)}MB`);
        this.sendAlert('high-memory-usage', {
          count: usedJSHeapSize,
          timestamp: Date.now()
        });
      }
    }
  }

  private cleanup(): void {
    const now = Date.now();
    const cutoff = Math.floor((now - 10000) / 1000); // 10 seconds ago
    
    // Clean up old render counts
    for (const [key] of this.renderCounts) {
      const timestamp = parseInt(key.split('-').pop() || '0');
      if (timestamp < cutoff) {
        this.renderCounts.delete(key);
      }
    }
    
    // Clean up old API call counts
    for (const [key] of this.apiCallCounts) {
      const timestamp = parseInt(key.split('-').pop() || '0');
      if (timestamp < cutoff) {
        this.apiCallCounts.delete(key);
      }
    }
  }

  private sendAlert(type: string, data: AlertData): void {
    // Call registered alert callbacks
    this.alertCallbacks.forEach(callback => {
      try {
        callback(type, data);
      } catch (error) {
        console.error('Error in performance alert callback:', error);
      }
    });

    // Integration with monitoring services
    if (typeof window !== 'undefined') {
      // Sentry integration
      if ('Sentry' in window) {
        (window as any).Sentry.captureMessage(`Performance Issue: ${type}`, {
          level: 'warning',
          extra: data
        });
      }
      
      // Custom analytics
      if ('gtag' in window) {
        (window as any).gtag('event', 'performance_issue', {
          event_category: 'Performance',
          event_label: type,
          value: data.count
        });
      }
    }
  }

  onAlert(callback: (type: string, data: AlertData) => void): () => void {
    this.alertCallbacks.push(callback);
    
    // Return unsubscribe function
    return () => {
      const index = this.alertCallbacks.indexOf(callback);
      if (index > -1) {
        this.alertCallbacks.splice(index, 1);
      }
    };
  }

  getMetrics() {
    return {
      renders: Object.fromEntries(this.renderCounts),
      apiCalls: Object.fromEntries(this.apiCallCounts),
      memorySnapshots: [...this.memorySnapshots],
      currentMemory: typeof window !== 'undefined' && 'performance' in window && 'memory' in (window.performance as any)
        ? (window.performance as any).memory.usedJSHeapSize
        : null
    };
  }

  updateThresholds(newThresholds: Partial<PerformanceThresholds>): void {
    Object.assign(this.thresholds, newThresholds);
  }
}

export const perfMonitor = new PerformanceMonitor();
export type { AlertData, PerformanceThresholds }; 